import React, { useEffect, useState, useRef } from "react";
import { api, streamSSE } from "@/lib/api";
import Markdown from "@/components/Markdown";
import { toast } from "sonner";
import {
  Search,
  Scale,
  Wrench,
  Sparkles,
  Loader2,
  ShieldAlert,
  ExternalLink,
  ArrowLeft,
  Info,
  Zap,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";

const PATENT_IDS = {
  tabScreen: "patent-tab-screen",
  tabFto: "patent-tab-fto",
  tabDesign: "patent-tab-design",
  queryInput: "patent-query-input",
  searchBtn: "patent-search-btn",
  deviceNameInput: "patent-device-name",
  deviceDescInput: "patent-device-desc",
  knownPatentsInput: "patent-known-list",
  ftoBtn: "patent-fto-btn",
  patentRefInput: "patent-ref-input",
  claimInput: "patent-claim-input",
  problemInput: "patent-problem-input",
  designBtn: "patent-design-btn",
  output: "patent-output",
};

const MODES = [
  { key: "screen", label: "Patent Screening", icon: Search, blurb: "Quick keyword-based sweep of potentially overlapping US patents." },
  { key: "fto",    label: "FTO Report",       icon: Scale,  blurb: "Full feature-by-feature Freedom-to-Operate screening with design-around recommendations." },
  { key: "design", label: "Design-Around",    icon: Wrench, blurb: "Ask for specific technical and documentation changes to avoid a known patent." },
];

const DISCLAIMER = (
  <div className="mb-6 flex items-start gap-3 border border-amber-500/40 bg-amber-500/8 p-4">
    <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
    <div className="text-sm leading-relaxed text-amber-100/90">
      <b>Preliminary screening only — not a legal opinion.</b> Qualmitta's patent tools use AI to
      surface potentially overlapping US patents as a starting point for research. Coverage of
      recently-issued or non-US patents may be limited. Every result must be independently verified
      on <a className="underline hover:text-slate-900" href="https://patents.google.com" target="_blank" rel="noreferrer">Google Patents</a>
      {" "}or <a className="underline hover:text-slate-900" href="https://ppubs.uspto.gov/pubwebapp/" target="_blank" rel="noreferrer">USPTO Patent Public Search</a>,
      and a registered patent attorney should conduct any formal Freedom-to-Operate opinion before
      product, design, or commercial decisions.
    </div>
  </div>
);

export default function PatentsPanel() {
  const [mode, setMode] = useState(null);        // 'screen' | 'fto' | 'design' | null
  const [content, setContent] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [ftoMeta, setFtoMeta] = useState(null);  // {doc_id, title}
  const [status, setStatus] = useState(null);    // /patents/status result
  const [runMeta, setRunMeta] = useState(null);  // last SSE meta {mode, uspto_hits}
  const [testingUspto, setTestingUspto] = useState(false);
  const [probeResult, setProbeResult] = useState(null);
  const outRef = useRef(null);

  useEffect(() => {
    api.get("/patents/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);

  const testUsptoConnection = async () => {
    setTestingUspto(true);
    setProbeResult(null);
    try {
      const { data } = await api.get("/patents/status?probe=true");
      setStatus({ ...data, probe: undefined });
      setProbeResult(data.probe || { ok: false, detail: "No probe result." });
      if (data.probe?.ok) toast.success("USPTO connection OK");
      else toast.error("USPTO probe failed — see banner");
    } catch (e) {
      setProbeResult({ ok: false, detail: e?.response?.data?.detail || "Request failed" });
      toast.error("Probe request failed");
    } finally {
      setTestingUspto(false);
    }
  };

  // Screen form
  const [query, setQuery] = useState("");

  // FTO form
  const [deviceName, setDeviceName] = useState("");
  const [deviceDesc, setDeviceDesc] = useState("");
  const [knownPatents, setKnownPatents] = useState("");

  // Design-around form
  const [patentRef, setPatentRef] = useState("");
  const [claim, setClaim] = useState("");
  const [problem, setProblem] = useState("");

  const reset = () => {
    setMode(null); setContent(""); setFtoMeta(null); setStreaming(false);
  };

  const runStream = async (path, body) => {
    setContent(""); setStreaming(true); setFtoMeta(null); setRunMeta(null);
    let buf = "";
    try {
      await streamSSE(path, body, (frame) => {
        if (frame.doc_id) setFtoMeta({ doc_id: frame.doc_id, title: frame.title });
        if (frame.meta) setRunMeta(frame.meta);
        if (frame.delta) {
          buf += frame.delta;
          setContent(buf);
          if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight;
        }
        if (frame.error) toast.error(frame.error);
      });
    } catch (e) {
      toast.info("Connection ended. If the report seems incomplete, run again.");
    } finally {
      setStreaming(false);
    }
  };

  const runScreen = () => {
    if (!query.trim()) return toast.error("Describe the device concept first");
    runStream("/patents/search", { query, max_results: 12 });
  };
  const runFto = () => {
    if (!deviceName.trim() || !deviceDesc.trim())
      return toast.error("Device name and description are required");
    const known = knownPatents.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    runStream("/patents/fto", { device_name: deviceName, device_description: deviceDesc, known_patents: known });
  };
  const runDesign = () => {
    if (!deviceName.trim() || !deviceDesc.trim() || !patentRef.trim())
      return toast.error("Device name, description, and patent reference are required");
    runStream("/patents/design-around", {
      device_name: deviceName,
      device_description: deviceDesc,
      patent_reference: patentRef,
      conflicting_claim: claim,
      problem_area: problem,
    });
  };

  // ---------- Gallery ----------
  if (!mode) {
    return (
      <div>
        <div className="mb-10">
          <div className="overline mb-3 text-[#2563EB]">PATENTS & IP · AI SCREENING</div>
          <h1 className="font-serif text-4xl font-semibold tracking-tight lg:text-5xl">
            Patent research and freedom-to-operate screening.
          </h1>
          <p className="mt-4 max-w-2xl text-slate-500">
            Surface potentially overlapping US patents, generate a preliminary FTO report, and get
            concrete design-around suggestions. Every response is a screening starting point —
            not a legal opinion.
          </p>
        </div>

        {DISCLAIMER}

        <div className="grid gap-px bg-slate-100 md:grid-cols-3">
          {MODES.map((m) => {
            const Icon = m.icon;
            return (
              <button
                key={m.key}
                data-testid={`patent-mode-${m.key}`}
                onClick={() => setMode(m.key)}
                className="group flex flex-col items-start bg-[#FAFAF7] p-6 text-left transition-all hover:bg-slate-50"
              >
                <Icon className="mb-8 h-7 w-7 text-[#2563EB] transition-transform group-hover:scale-110" strokeWidth={1.5} />
                <div className="overline mb-2 text-slate-500">MODE</div>
                <div className="font-serif text-xl font-semibold text-slate-900">{m.label}</div>
                <p className="mt-3 text-sm leading-relaxed text-slate-500">{m.blurb}</p>
                <span className="mt-6 overline text-[#2563EB] opacity-0 transition-opacity group-hover:opacity-100">
                  Start →
                </span>
              </button>
            );
          })}
        </div>

        <div
          data-testid="patents-status-banner"
          className="mt-8 border border-slate-200 bg-white p-4 text-sm text-slate-500"
        >
          <div className="flex items-start gap-3">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
            <div className="flex-1">
              {status?.mode === "live" ? (
                <>
                  <b className="text-emerald-300">Live USPTO data active</b> — every screening seeds
                  real patents from {status.live_source || "USPTO Open Data Portal"} before the AI
                  screens them.
                </>
              ) : (
                <>
                  <b className="text-slate-700">Data source:</b> AI-powered screening today (Claude
                  Sonnet 4.5). To enable live USPTO patent data, register a free USPTO ODP API key at{" "}
                  <a
                    href="https://data.uspto.gov/apis"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#93C5FD] underline"
                  >
                    data.uspto.gov/apis
                  </a>{" "}
                  and set <code className="font-mono text-[#93C5FD]">USPTO_API_KEY</code> in the
                  backend .env.
                </>
              )}
            </div>
            <button
              onClick={testUsptoConnection}
              disabled={testingUspto}
              data-testid="patents-selfcheck-btn"
              className="shrink-0 inline-flex items-center gap-1.5 border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-[#2563EB] hover:text-slate-900 disabled:opacity-50"
            >
              {testingUspto ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Zap className="h-3 w-3" />
              )}
              Test USPTO connection
            </button>
          </div>
          {probeResult && (
            <div
              data-testid="patents-selfcheck-result"
              className={`mt-3 flex items-start gap-2 border-l-2 pl-3 text-xs ${
                probeResult.ok
                  ? "border-emerald-400 text-emerald-200"
                  : "border-rose-400 text-rose-200"
              }`}
            >
              {probeResult.ok ? (
                <>
                  <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <div>
                    <b>Live · connected.</b> Sample query returned{" "}
                    {probeResult.sample_count || 0} patent
                    {probeResult.sample_count === 1 ? "" : "s"}.
                    {probeResult.sample_title && (
                      <span className="text-slate-500"> Top hit: "{probeResult.sample_title}"</span>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <div>
                    <b>Not connected.</b>{" "}
                    {probeResult.detail ||
                      "USPTO probe failed. Verify USPTO_API_KEY at data.uspto.gov."}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---------- Form + Output ----------
  const modeMeta = MODES.find((m) => m.key === mode);

  return (
    <div>
      <button
        onClick={reset}
        className="mb-6 inline-flex items-center gap-2 overline text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-3 w-3" /> All patent tools
      </button>

      <div className="grid gap-8 lg:grid-cols-[420px_1fr]">
        {/* FORM */}
        <div className="border border-slate-200 bg-white p-6">
          <div className="mb-6">
            <div className="overline mb-1 text-[#2563EB]">PATENT · AI SCREENING</div>
            <h2 className="font-serif text-2xl font-semibold">{modeMeta.label}</h2>
            <p className="mt-2 text-sm text-slate-500">{modeMeta.blurb}</p>
          </div>

          {mode === "screen" && (
            <>
              <label className="overline mb-2 block text-slate-500">Describe your device or feature</label>
              <textarea
                data-testid={PATENT_IDS.queryInput}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                rows={4}
                placeholder="e.g., Wearable continuous glucose monitor using a microneedle array, BLE transmission to a smartphone app, ML-based hypoglycemia prediction."
                className="w-full resize-none border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm placeholder:text-slate-300 focus:border-[#2563EB] focus:outline-none"
              />
              <button
                data-testid={PATENT_IDS.searchBtn}
                onClick={runScreen}
                disabled={streaming}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-50"
              >
                {streaming ? <><Loader2 className="h-4 w-4 animate-spin" /> Screening…</> : <><Sparkles className="h-4 w-4" /> Run patent screen</>}
              </button>
            </>
          )}

          {mode === "fto" && (
            <>
              <label className="overline mb-2 block text-slate-500">Device name *</label>
              <input
                data-testid={PATENT_IDS.deviceNameInput}
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                placeholder="CardioSense Pulse Ox v2"
                className="mb-4 w-full border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm placeholder:text-slate-300 focus:border-[#2563EB] focus:outline-none"
              />
              <label className="overline mb-2 block text-slate-500">Device description *</label>
              <textarea
                data-testid={PATENT_IDS.deviceDescInput}
                value={deviceDesc}
                onChange={(e) => setDeviceDesc(e.target.value)}
                rows={5}
                placeholder="Technology, features, workflow, form factor, connectivity, algorithms, sensors, materials…"
                className="mb-4 w-full resize-none border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm placeholder:text-slate-300 focus:border-[#2563EB] focus:outline-none"
              />
              <label className="overline mb-2 block text-slate-500">
                Known patents to include (optional)
              </label>
              <textarea
                data-testid={PATENT_IDS.knownPatentsInput}
                value={knownPatents}
                onChange={(e) => setKnownPatents(e.target.value)}
                rows={2}
                placeholder="US 11,234,567; US 10,987,654 — comma or newline separated"
                className="mb-4 w-full resize-none border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm placeholder:text-slate-300 focus:border-[#2563EB] focus:outline-none"
              />
              <button
                data-testid={PATENT_IDS.ftoBtn}
                onClick={runFto}
                disabled={streaming}
                className="inline-flex w-full items-center justify-center gap-2 bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-50"
              >
                {streaming ? <><Loader2 className="h-4 w-4 animate-spin" /> Running FTO…</> : <><Sparkles className="h-4 w-4" /> Run FTO screening</>}
              </button>
            </>
          )}

          {mode === "design" && (
            <>
              <label className="overline mb-2 block text-slate-500">Device name *</label>
              <input
                data-testid={PATENT_IDS.deviceNameInput}
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                placeholder="CardioSense Pulse Ox v2"
                className="mb-4 w-full border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm placeholder:text-slate-300 focus:border-[#2563EB] focus:outline-none"
              />
              <label className="overline mb-2 block text-slate-500">Device description *</label>
              <textarea
                data-testid={PATENT_IDS.deviceDescInput}
                value={deviceDesc}
                onChange={(e) => setDeviceDesc(e.target.value)}
                rows={4}
                placeholder="Short technical description of what your device does and how."
                className="mb-4 w-full resize-none border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm placeholder:text-slate-300 focus:border-[#2563EB] focus:outline-none"
              />
              <label className="overline mb-2 block text-slate-500">Patent reference *</label>
              <input
                data-testid={PATENT_IDS.patentRefInput}
                value={patentRef}
                onChange={(e) => setPatentRef(e.target.value)}
                placeholder="US 11,234,567"
                className="mb-4 w-full border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm placeholder:text-slate-300 focus:border-[#2563EB] focus:outline-none"
              />
              <label className="overline mb-2 block text-slate-500">Conflicting claim / element (optional)</label>
              <textarea
                data-testid={PATENT_IDS.claimInput}
                value={claim}
                onChange={(e) => setClaim(e.target.value)}
                rows={3}
                placeholder="Paste the claim text you're worried about, or describe the element."
                className="mb-4 w-full resize-none border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm placeholder:text-slate-300 focus:border-[#2563EB] focus:outline-none"
              />
              <label className="overline mb-2 block text-slate-500">Problem area (optional)</label>
              <input
                data-testid={PATENT_IDS.problemInput}
                value={problem}
                onChange={(e) => setProblem(e.target.value)}
                placeholder="e.g., signal processing algorithm, sensor material"
                className="mb-4 w-full border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm placeholder:text-slate-300 focus:border-[#2563EB] focus:outline-none"
              />
              <button
                data-testid={PATENT_IDS.designBtn}
                onClick={runDesign}
                disabled={streaming}
                className="inline-flex w-full items-center justify-center gap-2 bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-50"
              >
                {streaming ? <><Loader2 className="h-4 w-4 animate-spin" /> Working…</> : <><Sparkles className="h-4 w-4" /> Get design-around plan</>}
              </button>
            </>
          )}

          <div className="mt-4 flex items-start gap-2 border border-amber-500/30 bg-amber-500/5 p-3 text-xs leading-relaxed text-amber-100/85">
            <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>Screening only. Confirm each patent on Google Patents and consult a patent attorney before design or commercial decisions.</span>
          </div>
        </div>

        {/* OUTPUT */}
        <div className="border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div className="overline text-[#2563EB]">
                  {mode === "fto" ? "FTO SCREENING REPORT" : mode === "design" ? "DESIGN-AROUND PLAN" : "PATENT SCREEN"}
                </div>
                {runMeta?.mode === "live" && (
                  <span
                    data-testid="patents-run-live-badge"
                    className="inline-flex items-center gap-1 border border-emerald-400/50 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-300"
                  >
                    Live USPTO · {runMeta.uspto_hits || 0} hits
                  </span>
                )}
                {runMeta?.mode === "ai_screening" && (
                  <span className="inline-flex items-center border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    AI Screening
                  </span>
                )}
              </div>
              <div className="truncate font-serif text-lg font-semibold">
                {ftoMeta?.title || (content ? "Live output" : "Fill the form to start")}
              </div>
            </div>
            {ftoMeta?.doc_id && (
              <div className="shrink-0">
                <a
                  href="https://patents.google.com"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 border border-slate-200 px-3 py-2 text-xs text-slate-700 hover:border-[#2563EB]"
                >
                  Verify on Google Patents <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            )}
          </div>

          <div ref={outRef} data-testid={PATENT_IDS.output} className="max-h-[75vh] overflow-y-auto p-6 lg:p-10">
            {!content && !streaming && (
              <div className="flex h-64 flex-col items-center justify-center text-center text-slate-400">
                <Scale className="mb-4 h-8 w-8" strokeWidth={1.5} />
                <div className="overline">RESULTS APPEAR HERE</div>
              </div>
            )}
            {streaming && !content && (
              <div className="flex items-center gap-2 text-slate-500">
                <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
                <span className="ml-2 text-sm">Claude is screening patents…</span>
              </div>
            )}
            {content && <Markdown>{content}</Markdown>}
            {streaming && content && (
              <div className="mt-2 inline-flex items-center gap-2 text-xs text-slate-400">
                <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
